import {
  Card,
  Button,
  Input,
  Select,
  Form,
  Alert,
  Row,
  Col,
  Typography,
  Flex,
  Tag,
  Divider,
  Switch,
} from "antd"
import {
  SaveOutlined,
  HomeOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  CalendarOutlined,
  BuildOutlined,
  NumberOutlined,
  ToolOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons"
import { serializeQuery } from "helpers/service_helper"
import React, { useState, useReducer, useEffect, useMemo } from "react"

const { Title, Text } = Typography
const { TextArea } = Input

const ProjectUpdate = ({ rowData, mainProps }) => {
  // Initialize form state
  const initialFormState = useMemo(
    () => ({
      landlordName: rowData.landLordName,
      ownershipTypeEnum: rowData.ownershipTypeEnum,
      companyName: rowData.companyName,
      landlordNaCode: rowData.landlordNaCode,
      landlordPhoneNumber: rowData.landlordPhoneNumber,
      postalCode: rowData.postalCode,
      area: rowData.area,
      address: rowData.address,
      floorNumber: rowData.floorNumber,
      buildingType: rowData.buildingType,
      packageNeed: rowData.packageNeed,
      pipingTypeEnum: rowData.pipingTypeEnum,
      unitNumber: rowData.unitNumber,
      unitDes: rowData.unitDes,
      hasBurial: rowData.hasBurial ?? false,
    }),
    [rowData]
  )

  const [formInput, setFormInput] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    initialFormState
  )

  const [isErrorValid, setIsErrorValid] = useState(false)
  const [returnReadOnly, setReturnReadOnly] = useState(false)

  // Determine if form should be read-only
  useEffect(() => {
    const isReadOnly =
      rowData.gasProjectDetail?.gasProject?.projectLevelEnum === 2 ||
      (rowData.projectLevel !== 1 && rowData.projectLevel !== 4) ||
      (!rowData.accepted && (rowData.projectLevel === 1 || rowData.projectLevel === 4))
    setReturnReadOnly(isReadOnly)
  }, [rowData])

  // Reset form when rowData changes
  useEffect(() => {
    setFormInput(initialFormState)
  }, [initialFormState])

  const handleInput = (name, value) => {
    setFormInput({ [name]: value })
  }

  const handleSubmitForm = async () => {
    const { onUpdateGasProjectEngNew } = mainProps
    let mapToSave = {
      ...formInput,
      id: rowData.idGasProject,
      buildingTypeEnum: +formInput.buildingType,
      pipingTypeEnum: +formInput.pipingTypeEnum,
      unitNumber: +formInput.unitNumber,
      hasBurial: !!formInput.hasBurial,
    }
    await onUpdateGasProjectEngNew({
      process: mapToSave,
      queryString: serializeQuery({ idFile: rowData.idFile }),
    })
  }

  const isFloorNumberDisabled = useMemo(
    () =>
      Number(formInput.unitNumber) > 1 ||
      Number(formInput.unitNumber) <= 0 ||
      returnReadOnly,
    [formInput.unitNumber, returnReadOnly]
  )

  // Piping type options
  const pipingTypeOptions = [
    { value: 0, label: "روکار" },
    { value: 1, label: "توکار" },
  ]

  // Unit number options
  const unitNumberOptions = [
    { value: 1, label: "یک واحدی" },
    { value: 2, label: "دو واحدی" },
    { value: 3, label: "سه واحدی" },
    { value: 4, label: "چهار واحدی" },
    { value: 5, label: "پنج واحدی" },
    { value: 6, label: "شش واحدی" },
  ]

  // Building type options
  const buildingTypeOptions = [
    { value: 1, label: "مسکونی" },
    { value: 2, label: "تجاری" },
    { value: 3, label: "اداری" },
    { value: 5, label: "عمومی" },
  ]

  // Info card item component
  const InfoItem = ({ icon, label, value, color }) => (
    <Card
      size="small"
      style={{
        background: "#fafafa",
        borderRadius: 8,
        border: "1px solid #f0f0f0",
        height: "100%",
      }}
      bodyStyle={{ padding: "12px" }}
    >
      <Flex vertical gap={4}>
        <Flex align="center" gap={6}>
          <span style={{ color: color || "#667eea", fontSize: 16 }}>{icon}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {label}
          </Text>
        </Flex>
        <Text strong style={{ fontSize: 14, paddingRight: 22 }}>
          {value || "-"}
        </Text>
      </Flex>
    </Card>
  )

  return (
    <div>
      {/* Status Alert */}
      <Alert
        message={
          !rowData.accepted ? (
            <Flex align="center" gap={8}>
              <ClockCircleOutlined />
              <span>در انتظار قبول / رد پرونده</span>
            </Flex>
          ) : (
            <Flex align="center" gap={8}>
              <WarningOutlined />
              <span>
                بعد از اعلام نظر نمی توانید تعداد واحد و نوع لوله گذاری را تغییر
                دهید
              </span>
            </Flex>
          )
        }
        type={!rowData.accepted ? "info" : "warning"}
        showIcon={false}
        style={{
          marginBottom: 16,
          borderRadius: 10,
          border: "none",
        }}
      />

      {/* Project Info Card */}
      <Card
        style={{
          borderRadius: 12,
          marginBottom: 16,
          border: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Flex align="center" gap={8} style={{ marginBottom: 16 }}>
          <InfoCircleOutlined style={{ fontSize: 18, color: "#667eea" }} />
          <Title level={5} style={{ margin: 0 }}>
            اطلاعات پرونده
          </Title>
        </Flex>

        <Row gutter={[16, 8]}>
          {rowData.packageSerialNumber && (
            <Col xs={24}>
              <Card
                size="small"
                style={{
                  background: "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
                  borderRadius: 8,
                  border: "1px solid #667eea30",
                  marginBottom: 8,
                }}
              >
                <Flex align="center" gap={8}>
                  <AppstoreOutlined style={{ color: "#667eea" }} />
                  <Text>سریال پکیج:</Text>
                  <Tag color="purple">{rowData.packageSerialNumber}</Tag>
                </Flex>
              </Card>
            </Col>
          )}

          <Col xs={24} sm={12} md={6}>
            <InfoItem
              icon={<PhoneOutlined />}
              label="موبایل مجری"
              value={rowData.executorCellPhone}
              color="#52c41a"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <InfoItem
              icon={<CalendarOutlined />}
              label="تاریخ ثبت"
              value={rowData.solarRegisterDate}
              color="#1890ff"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <InfoItem
              icon={<IdcardOutlined />}
              label="کدملی مالک"
              value={rowData.landlordNaCode}
              color="#722ed1"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <InfoItem
              icon={<PhoneOutlined />}
              label="تماس مالک"
              value={rowData.landlordPhoneNumber}
              color="#eb2f96"
            />
          </Col>
        </Row>
      </Card>

      {/* Form Card */}
      <Card
        style={{
          borderRadius: 12,
          border: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
        bodyStyle={{ padding: "16px" }}
      >
        <Flex 
          align="center" 
          gap={8} 
          wrap="wrap"
          style={{ marginBottom: 20 }}
        >
          <FileTextOutlined style={{ fontSize: 18, color: "#667eea" }} />
          <Title level={5} style={{ margin: 0 }}>
            ویرایش اطلاعات
          </Title>
          {returnReadOnly && (
            <Tag color="orange" style={{ marginRight: "auto" }}>
              فقط خواندنی
            </Tag>
          )}
        </Flex>

        <Form layout="vertical" onFinish={handleSubmitForm}>
          <Row gutter={[12, 0]}>
            {/* Piping Type */}
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                label={
                  <Flex align="center" gap={6}>
                    <ToolOutlined style={{ color: "#667eea" }} />
                    <span>نوع لوله گذاری</span>
                  </Flex>
                }
                style={{ marginBottom: 12 }}
              >
                <Select
                  value={formInput.pipingTypeEnum ?? 0}
                  onChange={(value) => handleInput("pipingTypeEnum", value)}
                  disabled={returnReadOnly}
                  options={pipingTypeOptions}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            {/* Unit Number */}
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                label={
                  <Flex align="center" gap={6}>
                    <BuildOutlined style={{ color: "#667eea" }} />
                    <span>تعداد واحد</span>
                  </Flex>
                }
                style={{ marginBottom: 12 }}
              >
                <Select
                  value={formInput.unitNumber ?? 1}
                  onChange={(value) => handleInput("unitNumber", value)}
                  disabled={returnReadOnly}
                  options={unitNumberOptions}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            {/* Building Type */}
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                label={
                  <Flex align="center" gap={6}>
                    <HomeOutlined style={{ color: "#667eea" }} />
                    <span>نوع کاربری</span>
                  </Flex>
                }
                style={{ marginBottom: 12 }}
              >
                <Select
                  value={formInput.buildingType}
                  onChange={(value) => handleInput("buildingType", value)}
                  disabled={returnReadOnly}
                  options={buildingTypeOptions}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            {/* Postal Code */}
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                label={
                  <Flex align="center" gap={6}>
                    <EnvironmentOutlined style={{ color: "#667eea" }} />
                    <span>کد پستی</span>
                  </Flex>
                }
                validateStatus={isErrorValid ? "error" : ""}
                help={isErrorValid ? "کد پستی باید 10 رقم باشد" : ""}
                required
                style={{ marginBottom: 12 }}
              >
                <Input
                  value={formInput.postalCode}
                  onChange={(e) => {
                    handleInput("postalCode", e.target.value)
                    e.target.value.length !== 10
                      ? setIsErrorValid(true)
                      : setIsErrorValid(false)
                  }}
                  disabled={returnReadOnly}
                  type="number"
                  style={{ textAlign: "center" }}
                />
              </Form.Item>
            </Col>

            {/* Area */}
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                label={
                  <Flex align="center" gap={6}>
                    <AppstoreOutlined style={{ color: "#667eea" }} />
                    <span>زیربنای حرارتی (متر)</span>
                  </Flex>
                }
                required
                style={{ marginBottom: 12 }}
              >
                <Input
                  value={formInput.area}
                  onChange={(e) => handleInput("area", e.target.value)}
                  disabled={returnReadOnly}
                  type="number"
                  style={{ textAlign: "center" }}
                />
              </Form.Item>
            </Col>

            {/* Floor Number */}
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                label={
                  <Flex align="center" gap={6}>
                    <NumberOutlined style={{ color: "#667eea" }} />
                    <span>طبقه</span>
                  </Flex>
                }
                help={
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    0 = همکف
                  </Text>
                }
                required
                style={{ marginBottom: 12 }}
              >
                <Input
                  value={+formInput.unitNumber === 1 ? formInput.floorNumber : "0"}
                  onChange={(e) => handleInput("floorNumber", e.target.value)}
                  disabled={isFloorNumberDisabled}
                  type="number"
                  style={{ textAlign: "center" }}
                />
              </Form.Item>
            </Col>

            {/* Unit Description */}
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                label={
                  <Flex align="center" gap={6}>
                    <NumberOutlined style={{ color: "#667eea" }} />
                    <span>شماره واحد</span>
                  </Flex>
                }
                style={{ marginBottom: 12 }}
              >
                <Input
                  value={formInput.unitDes}
                  onChange={(e) => handleInput("unitDes", e.target.value)}
                  disabled={returnReadOnly}
                  style={{ textAlign: "center" }}
                />
              </Form.Item>
            </Col>

            {/* Has Burial */}
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                label={
                  <Flex align="center" gap={6}>
                    <ToolOutlined style={{ color: "#667eea" }} />
                    <span>دفنی</span>
                  </Flex>
                }
                style={{ marginBottom: 12 }}
              >
                <Switch
                  checked={formInput.hasBurial}
                  onChange={(checked) => handleInput("hasBurial", checked)}
                  disabled={returnReadOnly}
                  checkedChildren="دارد"
                  unCheckedChildren="ندارد"
                />
              </Form.Item>
            </Col>

            {/* Address */}
            <Col xs={24}>
              <Form.Item
                label={
                  <Flex align="center" gap={6}>
                    <EnvironmentOutlined style={{ color: "#667eea" }} />
                    <span>آدرس</span>
                  </Flex>
                }
                required
                style={{ marginBottom: 16 }}
              >
                <TextArea
                  value={formInput.address}
                  onChange={(e) => handleInput("address", e.target.value)}
                  disabled={returnReadOnly}
                  rows={2}
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "12px 0" }} />

          {/* Submit Button */}
          <Flex 
            justify="flex-start" 
            align="center" 
            gap={12}
            wrap="wrap"
          >
            {mainProps.loading ? (
              <Button
                type="primary"
                loading
                style={{
                  borderRadius: 8,
                  paddingInline: 24,
                  height: 40,
                }}
              >
                در حال ذخیره...
              </Button>
            ) : (
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                disabled={isErrorValid || returnReadOnly}
                style={{
                  borderRadius: 8,
                  paddingInline: 24,
                  height: 40,
                  background:
                    isErrorValid || returnReadOnly
                      ? undefined
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  boxShadow:
                    isErrorValid || returnReadOnly
                      ? undefined
                      : "0 4px 12px rgba(102, 126, 234, 0.4)",
                }}
              >
                ذخیره تغییرات
              </Button>
            )}

            {returnReadOnly && (
              <Tag
                color="orange"
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              >
                <InfoCircleOutlined style={{ marginLeft: 4 }} />
                امکان ویرایش وجود ندارد
              </Tag>
            )}
          </Flex>
        </Form>
      </Card>
    </div>
  )
}

export default ProjectUpdate
